export const ALL_WEEKDAYS: ReadonlySet<number> = new Set([0, 1, 2, 3, 4, 5, 6]);
export const WEEKDAYS_ONLY: ReadonlySet<number> = new Set([1, 2, 3, 4, 5]);

export const WEEKDAY_OPTIONS: ReadonlyArray<{ day: number; label: string; name: string }> = [
  { day: 1, label: 'M', name: 'Monday' },
  { day: 2, label: 'T', name: 'Tuesday' },
  { day: 3, label: 'W', name: 'Wednesday' },
  { day: 4, label: 'T', name: 'Thursday' },
  { day: 5, label: 'F', name: 'Friday' },
  { day: 6, label: 'S', name: 'Saturday' },
  { day: 0, label: 'S', name: 'Sunday' },
];

export function utcWeekday(isoDate: string): number {
  return new Date(`${isoDate}T00:00:00Z`).getUTCDay();
}

export function filterDailyByWeekdays<T extends { date: string }>(
  points: T[],
  selected: ReadonlySet<number>,
): T[] {
  if (selected.size === 0) {
    return [];
  }

  if (selected.size === ALL_WEEKDAYS.size) {
    return points;
  }

  return points.filter((point) => selected.has(utcWeekday(point.date)));
}

export function toggleWeekday(selected: ReadonlySet<number>, day: number): Set<number> {
  const next = new Set(selected);
  if (next.has(day)) {
    next.delete(day);
  } else {
    next.add(day);
  }

  return next;
}

export function meanRoundedPercent(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }

  const total = values.reduce((sum, value) => sum + Number(value), 0);
  return Math.min(100, Math.max(0, Math.round(total / values.length)));
}

export function dayParticipationPercent(participation: {
  wearables_percent: number;
  face_percent: number;
  voice_percent: number;
}): number {
  return (
    (Number(participation.wearables_percent) +
      Number(participation.face_percent) +
      Number(participation.voice_percent)) /
    3
  );
}

export function weekdayParticipationPercents(
  daily: ReadonlyArray<{
    date: string;
    participation: {
      wearables_percent: number;
      face_percent: number;
      voice_percent: number;
    };
  }>,
  windowDays: number,
  anchorDate: string,
): number[] {
  const buckets: number[][] = [[], [], [], [], [], [], []];
  denseParticipationSeries(daily, windowDays, anchorDate, ALL_WEEKDAYS).forEach((point) => {
    buckets[utcWeekday(point.date)].push(dayParticipationPercent(point.participation));
  });

  return buckets.map((values) => meanRoundedPercent(values));
}

const EMPTY_PARTICIPATION = {
  wearables_percent: 0,
  face_percent: 0,
  voice_percent: 0,
};

export function denseParticipationSeries(
  daily: ReadonlyArray<{
    date: string;
    participation: {
      wearables_percent: number;
      face_percent: number;
      voice_percent: number;
    };
  }>,
  windowDays: number,
  anchorDate: string,
  selected: ReadonlySet<number>,
): Array<{
  date: string;
  participation: {
    wearables_percent: number;
    face_percent: number;
    voice_percent: number;
  };
}> {
  if (selected.size === 0) {
    return [];
  }

  const byDate = new Map(daily.map((point) => [point.date, point.participation]));
  const end = new Date(`${anchorDate}T00:00:00Z`);
  const days = Math.max(1, windowDays);
  const series: Array<{
    date: string;
    participation: {
      wearables_percent: number;
      face_percent: number;
      voice_percent: number;
    };
  }> = [];

  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const day = new Date(end);
    day.setUTCDate(end.getUTCDate() - offset);
    if (!selected.has(day.getUTCDay())) {
      continue;
    }

    const iso = day.toISOString().slice(0, 10);
    const participation = byDate.get(iso);
    series.push({
      date: iso,
      participation: participation
        ? {
            wearables_percent: Number(participation.wearables_percent),
            face_percent: Number(participation.face_percent),
            voice_percent: Number(participation.voice_percent),
          }
        : { ...EMPTY_PARTICIPATION },
    });
  }

  return series;
}
